import { useEffect } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import { api } from "state/api";
import { addNativePushListeners, isNativePushSupported } from "./push";
import { isNativeApp } from "./index";
import { saveBiometricSession } from "./biometric";
import { universalLinkToPath } from "./links";
import { activateAccount, findSavedAccount } from "state/savedAccounts";

/**
 * The store app's runtime events, wired once inside the router (App.jsx):
 *
 *  - Push (iOS): a push landing while the app is open refreshes the bell's
 *    unread badge; a tap on a notification opens the lead (or list) it is
 *    about — first switching to the account it was for, when the phone has
 *    several signed in. The service worker does both on the web.
 *  - Universal links: a link to www.phiegcrm.com tapped in a lead email or a
 *    chat opens the app on that page instead of Safari (iOS reads
 *    /.well-known/apple-app-site-association for that). Both the launch link
 *    of a cold start and links while running are handled.
 *
 * Renders nothing; every listener is a no-op outside the native shell.
 */
export default function NativeEvents() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const store = useStore();
  const user = useSelector((state) => state.auth?.user);
  const token = useSelector((state) => state.auth?.token);
  const impersonator = useSelector((state) => state.auth?.impersonator);

  // Keep the Face ID sign-in copy of the session in step with the live one
  // (a no-op unless the person switched Face ID sign-in on). Never while an
  // admin is impersonating a client — that session is not the phone owner's.
  useEffect(() => {
    if (!isNativeApp() || !token || !user || impersonator) return;
    saveBiometricSession({ user, token });
  }, [token, user, impersonator]);

  useEffect(() => {
    if (!isNativePushSupported()) return undefined;

    let active = true;
    let remove = null;

    addNativePushListeners({
      onReceived: () => {
        dispatch(api.util.invalidateTags(["Notifications"]));
      },
      onTap: (url, _notification, { forUser } = {}) => {
        // A phone with several accounts: open the account the push was for
        // before opening its lead, or the lead would 404 under the wrong one.
        const current = store.getState().auth?.user?._id;
        const target = forUser && String(forUser) !== String(current)
          ? findSavedAccount(forUser)
          : null;
        if (target) {
          activateAccount(dispatch, target, { resetApi: api.util.resetApiState });
        } else {
          dispatch(api.util.invalidateTags(["Notifications"]));
        }
        if (url) navigate(url);
      },
    })
      .then((removeListeners) => {
        if (!active) removeListeners();
        else remove = removeListeners;
      })
      .catch(() => {
        /* a shell without the plugin: nothing to listen to */
      });

    return () => {
      active = false;
      if (remove) remove();
    };
  }, [navigate, dispatch, store]);

  useEffect(() => {
    if (!isNativeApp()) return undefined;

    let active = true;
    let handle = null;

    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const launch = await App.getLaunchUrl().catch(() => null);
        const launchPath = universalLinkToPath(launch?.url);
        if (active && launchPath) navigate(launchPath);

        const h = await App.addListener("appUrlOpen", ({ url }) => {
          const path = universalLinkToPath(url);
          if (path) navigate(path);
        });
        if (!active) h.remove();
        else handle = h;
      } catch {
        /* a shell without the App plugin */
      }
    })();

    return () => {
      active = false;
      if (handle) handle.remove();
    };
  }, [navigate]);

  return null;
}
